import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/pageTitle';
import { 
  Home, 
  FileText, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  CheckSquare,
  Speaker,
  BookOpen,
  BarChart3,
  TrendingUp,
  Users
} from 'lucide-react';
import { useAuth } from 'contexts/AuthContext';
import { useToast } from 'contexts/ToastContext';
import { getLegalAwareness } from 'services/legalAwarenessService';
import { getLawImplementations } from 'services/lawImplementationService';
import {
  LEGAL_AWAERENESS_REQUEST_STATUS,
  LAW_IMPLEMENTATION_REQUEST_STATUS
} from 'utils/constantValues';

// Stat item interface
interface StatItem {
  title: string;
  value: string;
  change: string;
  changeType: 'positive' | 'negative' | 'neutral';
  icon: React.ReactNode;
  bgColor: string;
  iconColor: string;
}

// Helper function to count items by status
const countByStatus = (items: any[], statusObj: any) => {
  const counts: { [key: string]: number } = {};
  Object.keys(statusObj).forEach(statusKey => {
    counts[statusObj[statusKey].label] = 0;
  });

  items.forEach(item => {
    const statusValue = item.status || item.current_status || item.request_status;
    Object.keys(statusObj).forEach(statusKey => {
      if (statusObj[statusKey].value === statusValue) {
        counts[statusObj[statusKey].label]++;
      }
    });
  });

  return counts;
};

// Helper to filter data by date range
const filterByDateRange = (items: any[], startDate: Date, endDate: Date) => {
  return items.filter(item => {
    const created_at = item.created_at || item.createdDate;
    if (!created_at) return false;
    const itemDate = new Date(created_at);
    return itemDate >= startDate && itemDate < endDate;
  });
};

// Helper functions to categorize status labels
const isActiveStatus = (label: string | undefined | null): boolean => {
  if (!label) return false;
  const activeKeywords = [
    'received', 'pending', 'underway', 'in progress', 'under review',
    'under analysis', 'submitted', 'submitted by stakeholder', 'submitted internally',
    'oral instruction logged', 'proposal submitted', 'proposal underway',
    'draft prepared', 'team appointed', 'identification in progress',
    'audit in progress', 'summary submitted', 'session scheduled',
    'team assigned', 'training manual submitted', 'training scheduled',
    'assigned to prosecutor', 'forwarded to', 'sent to'
  ];
  const lowerLabel = label.toLowerCase();
  return activeKeywords.some(keyword => lowerLabel.includes(keyword));
};

const isCompletedStatus = (label: string | undefined | null): boolean => {
  if (!label) return false;
  const completedKeywords = [
    'completed', 'published', 'final', 'archived', 'closed',
    'finalization complete', 'audit complete', 'final approval',
    'reference number submitted', 'letter reference number submitted'
  ];
  const lowerLabel = label.toLowerCase();
  return completedKeywords.some(keyword => lowerLabel.includes(keyword));
};

const isPendingReviewStatus = (label: string | undefined | null): boolean => {
  if (!label) return false;
  const pendingKeywords = [
    'revision', 'revision requested', 'revision updated', 'feedback',
    'awaiting approval', 'draft approved', 'proposal revision',
    'proposal update', 'draft - updated', 'proposal presentation feedback submitted',
    'additional info', 'waiting for', 'back to', 'reviewed by',
    'approved by head', 'follow-up'
  ];
  const lowerLabel = label.toLowerCase();
  return pendingKeywords.some(keyword => lowerLabel.includes(keyword));
};

// Calculate statistics from service data for a given period
const calculateStatsForPeriod = (data: any[], startDate: Date, endDate: Date, statusObj: any) => {
  let active = 0;
  let completed = 0;
  let pendingReview = 0;
  
  if (data && data.length > 0) {
    const periodData = filterByDateRange(data, startDate, endDate);
    if (periodData.length > 0) {
      const statusCounts = countByStatus(periodData, statusObj);
      Object.entries(statusCounts).forEach(([label, count]) => {
        const countNum = count as number;
        if (isActiveStatus(label)) {
          active += countNum;
        } else if (isCompletedStatus(label)) {
          completed += countNum;
        } else if (isPendingReviewStatus(label)) {
          pendingReview += countNum;
        }
      });
    }
  }
  
  return { active, completed, pendingReview };
};

// Get date range for a month
const getMonthRange = (date: Date): { start: Date; end: Date } => {
  const year = date.getFullYear();
  const month = date.getMonth();
  return {
    start: new Date(year, month, 1),
    end: new Date(year, month + 1, 1)
  };
};

// Calculate percentage change
const calculateChange = (current: number, previous: number): string => {
  if (previous === 0) return current > 0 ? '+100%' : '0%';
  const change = ((current - previous) / previous) * 100;
  return `${change >= 0 ? '+' : ''}${change.toFixed(0)}%`;
};

const ImpAwarenessCordinatorDashboard = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();

  // State for both services data
  const [serviceData, setServiceData] = useState<{
    legalAwareness: { data: any[]; loading: boolean };
    lawImplementation: { data: any[]; loading: boolean };
  }>({
    legalAwareness: { data: [], loading: true },
    lawImplementation: { data: [], loading: true }
  });

  // Fetch both services data on component mount
  useEffect(() => {
    const fetchServicesData = async () => {
      try {
        const [awarenessData, lawImplData] = await Promise.allSettled([
          getLegalAwareness(),
          getLawImplementations()
        ]);
        
        const awarenessItems = awarenessData.status === 'fulfilled' ? awarenessData.value : [];
        const lawImplItems = lawImplData.status === 'fulfilled' ? lawImplData.value : [];
        
        setServiceData({
          legalAwareness: {
            data: awarenessItems as any[],
            loading: false
          },
          lawImplementation: {
            data: lawImplItems as any[],
            loading: false
          }
        });
      } catch (error) {
        console.error('Error fetching services data:', error);
        showToast(t("ErrorLoadingData") || "Error loading data", "error");
        setServiceData(prev => ({
          ...prev,
          legalAwareness: { ...prev.legalAwareness, loading: false },
          lawImplementation: { ...prev.lawImplementation, loading: false }
        }));
      }
    };

    fetchServicesData();
  }, [showToast, t]);

  // Calculate totals
  const totalAwareness = serviceData.legalAwareness.data.length;
  const totalLawImplementation = serviceData.lawImplementation.data.length;
  const grandTotal = totalAwareness + totalLawImplementation;

  // Calculate month-over-month statistics
  const isLoading = serviceData.legalAwareness.loading || serviceData.lawImplementation.loading;
  const now = new Date();
  const currentMonthRange = getMonthRange(now);
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthRange = getMonthRange(lastMonthDate);

  // Calculate stats for both services
  const currentMonthAwarenessStats = isLoading ? { active: 0, completed: 0, pendingReview: 0 } : 
    calculateStatsForPeriod(serviceData.legalAwareness.data, currentMonthRange.start, currentMonthRange.end, LEGAL_AWAERENESS_REQUEST_STATUS);
  const lastMonthAwarenessStats = isLoading ? { active: 0, completed: 0, pendingReview: 0 } : 
    calculateStatsForPeriod(serviceData.legalAwareness.data, lastMonthRange.start, lastMonthRange.end, LEGAL_AWAERENESS_REQUEST_STATUS);

  const currentMonthLawImplStats = isLoading ? { active: 0, completed: 0, pendingReview: 0 } : 
    calculateStatsForPeriod(serviceData.lawImplementation.data, currentMonthRange.start, currentMonthRange.end, LAW_IMPLEMENTATION_REQUEST_STATUS);
  const lastMonthLawImplStats = isLoading ? { active: 0, completed: 0, pendingReview: 0 } : 
    calculateStatsForPeriod(serviceData.lawImplementation.data, lastMonthRange.start, lastMonthRange.end, LAW_IMPLEMENTATION_REQUEST_STATUS);

  // Combined stats
  const activeTasks = currentMonthAwarenessStats.active + currentMonthLawImplStats.active;
  const completedTasks = currentMonthAwarenessStats.completed + currentMonthLawImplStats.completed;
  const pendingReviewTasks = currentMonthAwarenessStats.pendingReview + currentMonthLawImplStats.pendingReview;

  const totalCurrentMonth = activeTasks + completedTasks + pendingReviewTasks;
  const totalLastMonth = (lastMonthAwarenessStats.active + lastMonthLawImplStats.active) + 
                        (lastMonthAwarenessStats.completed + lastMonthLawImplStats.completed) + 
                        (lastMonthAwarenessStats.pendingReview + lastMonthLawImplStats.pendingReview);

  const totalChange = calculateChange(totalCurrentMonth, totalLastMonth);
  const activeChange = calculateChange(activeTasks, 
    lastMonthAwarenessStats.active + lastMonthLawImplStats.active);
  const completedChange = calculateChange(completedTasks, 
    lastMonthAwarenessStats.completed + lastMonthLawImplStats.completed);
  const pendingChange = calculateChange(pendingReviewTasks, 
    lastMonthAwarenessStats.pendingReview + lastMonthLawImplStats.pendingReview);

  // Get status counts
  const awarenessStatusCounts = isLoading ? {} : countByStatus(serviceData.legalAwareness.data, LEGAL_AWAERENESS_REQUEST_STATUS);
  const lawImplStatusCounts = isLoading ? {} : countByStatus(serviceData.lawImplementation.data, LAW_IMPLEMENTATION_REQUEST_STATUS);

  // Stats for the dashboard
  const stats: StatItem[] = [
    {
      title: t("TotalServices") || "Total Services",
      value: isLoading ? '...' : grandTotal.toString(),
      change: totalChange,
      changeType: totalChange.startsWith('+') || totalChange === '0%' ? 'positive' : 'negative',
      icon: <BarChart3 className="w-6 h-6" />,
      bgColor: 'bg-indigo-50',
      iconColor: 'text-indigo-600',
    },
    {
      title: t("LegalAwarenessCreation") || "Legal Awareness Creation",
      value: isLoading ? '...' : totalAwareness.toString(),
      change: calculateChange(
        currentMonthAwarenessStats.active + currentMonthAwarenessStats.completed + currentMonthAwarenessStats.pendingReview,
        lastMonthAwarenessStats.active + lastMonthAwarenessStats.completed + lastMonthAwarenessStats.pendingReview
      ),
      changeType: 'positive',
      icon: <Speaker className="w-6 h-6" />,
      bgColor: 'bg-pink-50',
      iconColor: 'text-pink-600',
    },
    {
      title: t("LawImplementation") || "Law Implementation",
      value: isLoading ? '...' : totalLawImplementation.toString(),
      change: calculateChange(
        currentMonthLawImplStats.active + currentMonthLawImplStats.completed + currentMonthLawImplStats.pendingReview,
        lastMonthLawImplStats.active + lastMonthLawImplStats.completed + lastMonthLawImplStats.pendingReview
      ),
      changeType: 'positive',
      icon: <CheckSquare className="w-6 h-6" />,
      bgColor: 'bg-cyan-50',
      iconColor: 'text-cyan-600',
    },
    {
      title: t("WaitingForAssignment") || "Waiting for Assignment",
      value: isLoading ? '...' : activeTasks.toString(),
      change: activeChange,
      changeType: 'positive',
      icon: <Clock className="w-6 h-6" />,
      bgColor: 'bg-yellow-50',
      iconColor: 'text-yellow-600',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <PageTitle
        text={t("ImpAwarenessCordinatorDashboard") || "Implementation & Awareness Coordinator Dashboard"}
        icon={<Home />}
        iconBgColor="bg-gray-100"
        iconColor="text-gray-800"
        subtitle={t("ImpAwarenessCordinatorDashboardSubtitle") || "Overview of Legal Awareness Creation and Law Implementation services"}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                <p className={`text-sm mt-1 ${
                  stat.changeType === 'positive' ? 'text-green-600' : 
                  stat.changeType === 'negative' ? 'text-red-600' : 
                  'text-gray-500'
                }`}>
                  {t("ChangeFromLastMonth", { change: stat.change })}
                </p>
              </div>
              <div className={`p-3 rounded-full ${stat.bgColor}`}>
                <span className={stat.iconColor}>{stat.icon}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Services Overview Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Legal Awareness Creation Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="bg-pink-100 p-2 rounded-lg">
                <Speaker className="w-6 h-6 text-pink-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t("LegalAwarenessCreation") || "Legal Awareness Creation"}</h3>
                <p className="text-sm text-gray-600">{t("PublicAwarenessCampaigns")}</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-3xl font-bold text-gray-900">
                {isLoading ? '...' : totalAwareness}
              </span>
              <span className="text-sm text-gray-500">{t("campaigns")}</span>
            </div>
            
            {/* Awareness Stats Breakdown */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-pink-50 rounded-lg p-4">
                <p className="text-sm text-pink-600 font-medium">{t("Active") || "Active"}</p>
                <p className="text-xl font-bold text-pink-900">
                  {isLoading ? '...' : currentMonthAwarenessStats.active}
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-green-600 font-medium">{t("Completed") || "Completed"}</p>
                <p className="text-xl font-bold text-green-900">
                  {isLoading ? '...' : currentMonthAwarenessStats.completed}
                </p>
              </div>
            </div>

            {/* Status badges */}
            {!isLoading && totalAwareness > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs font-medium text-gray-500 mb-2">{t("StatusBreakdown")}</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(awarenessStatusCounts)
                    .filter(([_, count]) => (count as number) > 0)
                    .map(([status, count]) => {
                      let badgeColor = 'bg-gray-100 text-gray-800';
                      if (isActiveStatus(status)) badgeColor = 'bg-yellow-100 text-yellow-800';
                      else if (isCompletedStatus(status)) badgeColor = 'bg-green-100 text-green-800';
                      else if (isPendingReviewStatus(status)) badgeColor = 'bg-red-100 text-red-800';
                      return (
                        <span key={status} className={`px-3 py-1 rounded-full text-xs font-medium ${badgeColor}`}>
                          {status}: {count}
                        </span>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Law Implementation Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="bg-cyan-100 p-2 rounded-lg">
                <CheckSquare className="w-6 h-6 text-cyan-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t("LawImplementation") || "Law Implementation"}</h3>
                <p className="text-sm text-gray-600">{t("ImplementationMonitoring")}</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-3xl font-bold text-gray-900">
                {isLoading ? '...' : totalLawImplementation}
              </span>
              <span className="text-sm text-gray-500">{t("monitors")}</span>
            </div>
            
            {/* Law Implementation Stats Breakdown */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-cyan-50 rounded-lg p-4">
                <p className="text-sm text-cyan-600 font-medium">{t("Active") || "Active"}</p>
                <p className="text-xl font-bold text-cyan-900">
                  {isLoading ? '...' : currentMonthLawImplStats.active}
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-green-600 font-medium">{t("Completed") || "Completed"}</p>
                <p className="text-xl font-bold text-green-900">
                  {isLoading ? '...' : currentMonthLawImplStats.completed}
                </p>
              </div>
            </div>

            {/* Status badges */}
            {!isLoading && totalLawImplementation > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs font-medium text-gray-500 mb-2">{t("StatusBreakdown")}</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(lawImplStatusCounts)
                    .filter(([_, count]) => (count as number) > 0)
                    .map(([status, count]) => {
                      let badgeColor = 'bg-gray-100 text-gray-800';
                      if (isActiveStatus(status)) badgeColor = 'bg-yellow-100 text-yellow-800';
                      else if (isCompletedStatus(status)) badgeColor = 'bg-green-100 text-green-800';
                      else if (isPendingReviewStatus(status)) badgeColor = 'bg-red-100 text-red-800';
                      return (
                        <span key={status} className={`px-3 py-1 rounded-full text-xs font-medium ${badgeColor}`}>
                          {status}: {count}
                        </span>
                      );
                    })}
                </div>
              </div>
            )}
           </div>
         </div>
       </div>
     </div>
   );
 };

export default ImpAwarenessCordinatorDashboard;
